import {
  isBuilderHtmlCached,
  prefetchBuilderSectionHtml,
  warmPbAssetsWhenIdle,
  warmTagModulesWhenIdle,
} from '@theme/pb-builder-launch';
import {
  isBuilderBootReady,
  readNavigatorConnection,
  resolvePrefetchPolicy,
} from '@theme/pb-builder-prefetch-policy';

const VIEWPORT_ROOT_MARGIN = '200px 0px';
const BOOT_WAIT_MS = 8000;
const MAX_SPECULATIVE_BUILDERS = 2;

/** @type {ReturnType<typeof resolvePrefetchPolicy>} */
let policy = resolvePrefetchPolicy();

/** @type {string[]} */
const prefetchQueue = [];
const queuedHandles = new Set();
let activePrefetches = 0;
/** @type {IntersectionObserver | null} */
let viewportObserver = null;
const observedCards = new WeakSet();
let preloadStarted = false;
let prefetchedCount = 0;

function canPrefetch() {
  return document.readyState === 'complete' && document.visibilityState !== 'hidden' &&
    !document.body.classList.contains('builder-open');
}

function maxConcurrent() {
  return policy.maxConcurrent;
}

function firstRowLimit() {
  return policy.firstRowLimit;
}

function enqueuePrefetch(handle) {
  if (!canPrefetch() || prefetchedCount + prefetchQueue.length >= MAX_SPECULATIVE_BUILDERS) return;
  if (!handle || isBuilderHtmlCached(handle) || queuedHandles.has(handle)) return;
  queuedHandles.add(handle);
  prefetchQueue.push(handle);
  drainPrefetchQueue();
}

function drainPrefetchQueue() {
  while (canPrefetch() && activePrefetches < maxConcurrent() && prefetchQueue.length > 0) {
    const handle = prefetchQueue.shift();
    if (!handle) continue;
    activePrefetches += 1;
    prefetchedCount += 1;
    prefetchBuilderSectionHtml(handle).finally(() => {
      activePrefetches -= 1;
      queuedHandles.delete(handle);
      drainPrefetchQueue();
    });
  }
}

function getBuilderTriggers() {
  return document.querySelectorAll('[data-pb-card-open][data-has-builder="true"]:not([disabled])');
}

function prefetchFromTrigger(trigger) {
  const handle = trigger.dataset.productHandle;
  if (handle) enqueuePrefetch(handle);
}

function prefetchFirstRow() {
  const limit = firstRowLimit();
  if (limit <= 0) return;

  const triggers = getBuilderTriggers();
  const seen = new Set();
  let count = 0;

  triggers.forEach((trigger) => {
    if (count >= limit) return;
    const handle = trigger.dataset.productHandle;
    if (!handle || seen.has(handle)) return;
    seen.add(handle);
    count += 1;
    enqueuePrefetch(handle);
  });
}

function observeBuilderCard(card) {
  if (!card || observedCards.has(card)) return;
  if (!card.querySelector('[data-pb-card-open][data-has-builder="true"]')) return;
  observedCards.add(card);
  viewportObserver?.observe(card);
}

function observeAllBuilderCards() {
  if (!viewportObserver) return;
  document.querySelectorAll('.product-grid__item, product-card').forEach((card) => {
    observeBuilderCard(card);
  });
}

function initViewportObserver() {
  if (!policy.viewportEnabled || !('IntersectionObserver' in window)) return;

  viewportObserver = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        const card = entry.target;
        const trigger = card.querySelector('[data-pb-card-open][data-has-builder="true"]');
        if (trigger) prefetchFromTrigger(trigger);
        viewportObserver?.unobserve(card);
      });
    },
    { rootMargin: VIEWPORT_ROOT_MARGIN, threshold: 0.01 }
  );

  observeAllBuilderCards();
}

function initPreload() {
  if (!canPrefetch()) return;
  if (preloadStarted) return;
  if (!isBuilderBootReady(window)) return;
  if (!getBuilderTriggers().length) return;

  preloadStarted = true;
  policy = resolvePrefetchPolicy(readNavigatorConnection(navigator));
  if (!policy.viewportEnabled && policy.firstRowLimit === 0) return;

  prefetchFirstRow();
  initViewportObserver();
  warmPbAssetsWhenIdle();
  warmTagModulesWhenIdle();

  const productGrid = document.querySelector('.product-grid, results-list');
  if (gridObserver && productGrid) {
    gridObserver.observe(productGrid, { childList: true, subtree: true });
  }
}

function handleNewContent() {
  if (!preloadStarted) return;
  if (!policy.viewportEnabled && policy.firstRowLimit === 0) return;
  observeAllBuilderCards();
  prefetchFirstRow();
}

/** @type {MutationObserver | null} */
const gridObserver =
  typeof MutationObserver !== 'undefined'
    ? new MutationObserver(() => {
        handleNewContent();
      })
    : null;

function waitForBootThenInit() {
  if (isBuilderBootReady(window)) {
    initPreload();
    return;
  }

  const onReady = () => {
    document.removeEventListener('pb:boot-ready', onReady);
    initPreload();
  };
  document.addEventListener('pb:boot-ready', onReady, { once: true });

  const deadline = Date.now() + BOOT_WAIT_MS;
  const poll = () => {
    if (isBuilderBootReady(window)) {
      document.removeEventListener('pb:boot-ready', onReady);
      initPreload();
      return;
    }
    if (Date.now() >= deadline) return;
    setTimeout(poll, 50);
  };
  poll();
}

function schedulePreload() {
  const run = () => { if (canPrefetch()) waitForBootThenInit(); };
  if (typeof window.requestIdleCallback === 'function') window.requestIdleCallback(run, { timeout: 3000 });
  else setTimeout(run, 1500);
}

if (document.readyState === 'complete') schedulePreload();
else window.addEventListener('load', schedulePreload, { once: true });

document.addEventListener('shopify:section:load', () => {
  if (!preloadStarted && isBuilderBootReady(window)) initPreload();
  else handleNewContent();
});
